import type { InfraAgent } from "@/types";
import type { ApiAgent } from "@/lib/api";

/**
 * Transform API agent data to frontend InfraAgent format
 */
export function transformApiAgent(apiAgent: ApiAgent): InfraAgent {
  return {
    id: apiAgent.id,
    title: apiAgent.title,
    description: apiAgent.description,
    tags: apiAgent.tags || [],
    competencies: apiAgent.competencies || [],
    githubUrl: apiAgent.githubUrl,
    websiteUrl: apiAgent.websiteUrl,
    markdownContent: apiAgent.markdownContent,
    stats: {
      likes: apiAgent.stats?.likes || apiAgent.likes || 0,
      views: apiAgent.stats?.views || apiAgent.views || 0,
    },
    authorName: apiAgent.authorName,
    authorEmail: apiAgent.authorEmail,
    createdAt: apiAgent.createdAt,
    updatedAt: apiAgent.updatedAt,
    status: apiAgent.status,
    // Map new top-level metadata fields
    license: apiAgent.license,
    githubStars: apiAgent.githubStars,
    primaryLanguage: apiAgent.primaryLanguage,
    repositoryCreatedAt: apiAgent.repositoryCreatedAt,
    repositoryUpdatedAt: apiAgent.repositoryUpdatedAt,
    forksCount: apiAgent.forksCount,
    openIssuesCount: apiAgent.openIssuesCount,
    watchersCount: apiAgent.watchersCount,
    hasWiki: apiAgent.hasWiki,
    hasPages: apiAgent.hasPages,
    githubTopics: apiAgent.githubTopics,
    // Legacy metadata object for backward compatibility
    metadata: apiAgent.metadata || {
      github_stars: apiAgent.githubStars || 0,
      license: apiAgent.license || "Unknown",
      language: apiAgent.primaryLanguage || "Unknown",
    },
  };
}

/**
 * Transform frontend AgentFormData to API format
 */
export function transformToApiAgent(formData: {
  title: string;
  description: string;
  tags: string[];
  githubUrl?: string;
  websiteUrl?: string;
  markdownContent?: string;
  authorName: string;
  authorEmail?: string;
}): {
  name: string;
  description: string;
  competency: string;
  tags: string[];
  githubUrl?: string;
  websiteUrl?: string;
  markdownContent?: string;
  authorName: string;
  authorEmail?: string;
} {
  // For now, we'll set a default competency. In a real app, this should be selected by the user
  const defaultCompetency = inferCompetencyFromTags(formData.tags);

  return {
    name: formData.title,
    description: formData.description,
    competency: defaultCompetency,
    tags: formData.tags,
    githubUrl: formData.githubUrl,
    websiteUrl: formData.websiteUrl,
    markdownContent: formData.markdownContent,
    authorName: formData.authorName,
    authorEmail: formData.authorEmail,
  };
}

/**
 * Infer competency from tags (simple mapping)
 */
function inferCompetencyFromTags(tags: string[]): string {
  const competencyMap: Record<string, string> = {
    kubernetes: "container",
    docker: "container",
    aws: "cloud-platform",
    azure: "cloud-platform",
    gcp: "cloud-platform",
    terraform: "iac",
    cloudformation: "iac",
    serverless: "serverless",
    lambda: "serverless",
    monitoring: "monitoring",
    prometheus: "monitoring",
    grafana: "monitoring",
    security: "security",
    database: "database",
    postgresql: "database",
    mysql: "database",
    mongodb: "database",
    "ci/cd": "cicd",
    deployment: "deployment",
    migration: "database",
    storage: "storage",
    network: "network",
    "disaster-recovery": "disaster-recovery",
    "cost-optimization": "cost-optimization",
  };

  for (const tag of tags) {
    const competency = competencyMap[tag.toLowerCase()];
    if (competency) {
      return competency;
    }
  }

  // Default competency if no match found
  return "deployment";
}

/**
 * Format date for display
 */
export function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "Unknown";
  }
}

/**
 * Format numbers with proper abbreviations
 */
export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + "M";
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + "K";
  }
  return num.toString();
}

/**
 * Debounce function for search
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

/**
 * Generate a unique ID (simple implementation)
 */
export function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

/**
 * Validate URL format
 */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Sanitize tag input
 */
export function sanitizeTag(tag: string): string {
  return tag
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Extract domain from URL
 */
export function extractDomain(url: string): string {
  try {
    const domain = new URL(url).hostname;
    return domain.replace(/^www\./, "");
  } catch {
    return url;
  }
}
